import { useState, useEffect } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { Download, CheckSquare, Square } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OrderReportScreen = () => {
  const [reportData, setReportData] = useState({});
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('to-order'); // 'to-order' or 'history'

  useEffect(() => {
    fetchReport();
  }, []);

  const fetchReport = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/reports/by-supplier`);
      setReportData(response.data);
    } catch (error) {
      console.error('Error fetching report:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleOrdered = async (supplier, description, currentStatus) => {
    // If unchecking an item (in History view), require confirmation
    if (currentStatus) {
      const confirmUncheck = window.confirm(
        `⚠️ UNCHECK ITEM\n\n` +
        `Are you sure you want to mark this as NOT ordered?\n\n` +
        `"${description}"\n\n` +
        `This will move it back to the "To Order" list.`
      );
      
      if (!confirmUncheck) {
        return; // Cancel the action
      }
    }
    
    try {
      await axios.post(`${API}/reports/mark-ordered`, null, {
        params: {
          supplier,
          description,
          ordered: !currentStatus
        }
      });
      
      // Update local state
      setReportData(prev => ({
        ...prev,
        [supplier]: {
          ...prev[supplier],
          [description]: {
            ...prev[supplier][description],
            ordered: !currentStatus
          }
        }
      }));

      // Workflow hint
      if (!currentStatus) {
        toast.success('Marked as ordered', {
          description: `📋 Next: place this order with ${supplier} (call, email, or Lightspeed PO). Once it arrives, hit "Received" in the Incoming tab.`,
          duration: 8000,
        });
      } else {
        toast('Moved back to To Order', {
          description: 'This item is now in the active To Order list again.',
        });
      }
    } catch (error) {
      console.error('Error updating ordered status:', error);
      toast.error('Failed to update — please try again.');
    }
  };

  const handleExportExcel = () => {
    try {
      // Prepare data for Excel
      const excelData = [];
      
      Object.entries(reportData).forEach(([supplier, items]) => {
        excelData.push({
          'Supplier': supplier,
          'UPC': '',
          'Product': '',
          'Quantity': '',
          'Ordered': ''
        });
        
        Object.entries(items).forEach(([description, data]) => {
          excelData.push({
            'Supplier': '',
            'UPC': data.upc || '',
            'Product': description,
            'Quantity': data.qty,
            'Ordered': data.ordered ? 'Yes' : 'No'
          });
        });
        
        // Add empty row between suppliers
        excelData.push({
          'Supplier': '',
          'UPC': '',
          'Product': '',
          'Quantity': '',
          'Ordered': ''
        });
      });
      
      // Create worksheet
      const ws = XLSX.utils.json_to_sheet(excelData);
      
      // Set column widths
      ws['!cols'] = [
        { wch: 25 }, // Supplier
        { wch: 16 }, // UPC
        { wch: 60 }, // Product
        { wch: 10 }, // Quantity
        { wch: 10 }  // Ordered
      ];
      
      // Create workbook
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Order Report');
      
      // Generate filename with date
      const date = new Date().toISOString().split('T')[0];
      const filename = `order-report-${date}.xlsx`;
      
      // Download
      XLSX.writeFile(wb, filename);
      
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      alert('Failed to export to Excel');
    }
  };

  if (loading) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <p className="text-center text-gray-500">Loading report...</p>
      </div>
    );
  }

  const suppliers = Object.keys(reportData);

  if (suppliers.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4" style={{fontFamily: 'Manrope, sans-serif'}}>Order Report by Supplier</h2>
        <p className="text-center text-gray-500 py-8">No pending orders</p>
      </div>
    );
  }

  // Filter suppliers based on view mode
  const filteredSuppliers = suppliers.filter(supplier => {
    const items = Object.entries(reportData[supplier]);
    
    if (viewMode === 'to-order') {
      // Show only suppliers with unchecked items
      return items.some(([_, data]) => !data.ordered);
    } else {
      // Show only suppliers with checked items
      return items.some(([_, data]) => data.ordered);
    }
  });

  // Function to filter items within a supplier
  const getFilteredItems = (supplier) => {
    return Object.entries(reportData[supplier]).filter(([_, data]) => {
      if (viewMode === 'to-order') {
        return !data.ordered; // Show unchecked items
      } else {
        return data.ordered; // Show checked items
      }
    });
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900" style={{fontFamily: 'Manrope, sans-serif'}}>Order Report by Supplier</h2>
          <p className="text-sm text-gray-600 mt-1">
            {viewMode === 'to-order' ? 'Items you need to order' : 'Items already ordered'}
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          {/* View Mode Toggle */}
          <div className="flex gap-2 bg-gray-100 rounded-lg p-1">
            <button
              data-testid="view-to-order"
              onClick={() => setViewMode('to-order')}
              className={`px-4 py-2 rounded-md font-semibold text-sm transition-colors ${
                viewMode === 'to-order'
                  ? 'bg-white text-[#0066FF] shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <span className="hidden sm:inline">To Order</span>
              <span className="sm:hidden">Order</span>
            </button>
            <button
              data-testid="view-history"
              onClick={() => setViewMode('history')}
              className={`px-4 py-2 rounded-md font-semibold text-sm transition-colors ${
                viewMode === 'history'
                  ? 'bg-white text-[#0066FF] shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <span className="hidden sm:inline">History</span>
              <span className="sm:hidden">Done</span>
            </button>
          </div>
          
          {/* Action Buttons */}
          {viewMode === 'to-order' && (
            <button
              data-testid="export-excel-button"
              onClick={handleExportExcel}
              className="bg-[#10B981] hover:bg-[#059669] text-white border border-[#10B981] font-semibold rounded-lg h-12 px-6 text-base transition-colors flex items-center gap-2"
            >
              <Download size={18} />
              <span className="hidden sm:inline">Export Excel</span>
              <span className="sm:hidden">Export</span>
            </button>
          )}
          
          <button
            data-testid="refresh-report-button"
            onClick={fetchReport}
            className="bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 font-semibold rounded-lg h-12 px-6 text-base transition-colors"
          >
            Refresh
          </button>
        </div>
      </div>

      {filteredSuppliers.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500 text-lg">
            {viewMode === 'to-order' 
              ? '✅ All items ordered! Check "History" to see what you ordered.'
              : 'No order history yet. Check off items as you order them.'}
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {filteredSuppliers.map((supplier, index) => {
            const filteredItems = getFilteredItems(supplier);
            const itemCount = filteredItems.length;
            
            return (
              <div key={index} data-testid={`supplier-section-${index}`} className="border border-gray-200 rounded-lg p-4">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-xl font-bold text-gray-900" style={{fontFamily: 'Manrope, sans-serif'}}>{supplier}</h3>
                  <span className="text-sm text-gray-600">
                    {itemCount} item{itemCount !== 1 ? 's' : ''}
                  </span>
                </div>
                <div className="space-y-2">
                  {filteredItems.map(([description, data], itemIndex) => (
                    <div
                      key={itemIndex}
                      data-testid={`report-item-${index}-${itemIndex}`}
                      className={`flex items-center gap-3 py-2 px-3 rounded border transition-colors ${
                        data.ordered 
                          ? 'bg-green-50 border-green-200' 
                          : 'bg-white border-gray-100 hover:bg-gray-50'
                      }`}
                    >
                      <button
                        data-testid={`checkbox-${index}-${itemIndex}`}
                        onClick={() => handleToggleOrdered(supplier, description, data.ordered)}
                        className="flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-[#0066FF] rounded"
                      >
                        {data.ordered ? (
                          <CheckSquare size={24} className="text-[#10B981]" />
                        ) : (
                          <Square size={24} className="text-gray-400 hover:text-gray-600" />
                        )}
                      </button>
                      <span className={`flex-1 pr-4 ${data.ordered ? 'text-gray-600 line-through' : 'text-gray-900'}`}>
                        {description}
                        {data.upc && (
                          <span
                            data-testid={`upc-${index}-${itemIndex}`}
                            className="ml-2 inline-block font-mono text-[11px] text-gray-500 bg-gray-100 border border-gray-200 rounded px-1.5 py-0.5 align-middle"
                            title="UPC"
                          >
                            {data.upc}
                          </span>
                        )}
                      </span>
                      <span className="font-semibold text-gray-900 text-lg whitespace-nowrap">
                        {data.qty}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default OrderReportScreen;
